import React, { useContext, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { AuthContext, type AuthContextValue } from '~/auth';

import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, Box, Button, Container, Dialog, IconButton, alpha } from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER } from '~/consts/roles';

import UsersService from '~/services/UsersService';

import EnterCodeModal from '../../Users/UserDetails/VerifyCustomer/EnterCodeModal';
import OtpSuccessModal from '../../Users/UserDetails/VerifyCustomer/OtpSuccessModal';
import SendSmsModal from '../../Users/UserDetails/VerifyCustomer/SendSmsModal';

import { useStickyVerifyCustomer } from './useStickyVerifyCustomer';

const STEP_SEND_SMS = 'SEND_SMS';
const STEP_ENTER_CODE = 'ENTER_CODE';
const STEP_SUCCESS = 'SUCCESS';

export type Step = 'SEND_SMS' | 'ENTER_CODE' | 'SUCCESS';

function StickyVerifyCustomer() {
  const { user } = useContext<AuthContextValue>(AuthContext);
  const { customerId, isVerified, closeStickyVerify, markAsVerified } = useStickyVerifyCustomer();
  const [currentStep, setCurrentStep] = useState<Step | null>(null);

  // If user is not authenticated, clear the cookie to prevent infinite loops
  useEffect(() => {
    if (!user && customerId) {
      closeStickyVerify();
    }
  }, [user, customerId, closeStickyVerify]);

  // Call useQuery before any early returns to maintain hook order
  const {
    data: customer,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: async ({ signal }) => {
      return await UsersService.getUser(customerId, { signal });
    },
    enabled: !!customerId && !!user, // Only fetch if we have both customerId and authenticated user
  });

  // If user is not authenticated, don't show anything
  if (!user) {
    return null;
  }

  const isModalOpen = Boolean(currentStep);
  const hasPhoneNumber = customer?.phone_prefix && customer?.phone;

  const handleOpenSmsVerification = () => {
    setCurrentStep(STEP_SEND_SMS);
  };

  const handleCloseModal = () => {
    setCurrentStep(null);
  };

  const handleVerificationSuccess = () => {
    markAsVerified();
    setCurrentStep(null);
  };

  // Return null if no customerId is present
  if (!customerId) {
    return null;
  }

  if (isLoading) {
    return (
      <Alert severity="info" sx={{ borderRadius: 0 }}>
        Loading customer details...
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 0 }}>
        Error loading customer details
        <IconButton size="small" onClick={closeStickyVerify} sx={{ ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Alert>
    );
  }

  if (!customer) {
    return null;
  }

  const customerName = customer.fullName || customer.full_name || 'Unknown Customer';

  // Show success banner if verified=true
  if (isVerified) {
    return (
      <Box
        sx={{
          bgcolor: (theme) => alpha(theme.palette.success.light, 0.12),
          paddingY: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Alert
            severity="success"
            action={
              <IconButton size="small" onClick={closeStickyVerify}>
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Box>
              <strong>{customerName}</strong> has already been verified, no need to re-verify them.
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  // Show verification banner if not verified
  return (
    <Box
      sx={{
        bgcolor: (theme) => alpha(theme.palette.info.light, 0.12),
        paddingY: 0,
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <Alert
          severity="info"
          action={
            <Box display="flex" alignItems="center" gap={1}>
              <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenSmsVerification}
                  disabled={isModalOpen || !hasPhoneNumber}
                >
                  Verify
                </Button>
              </PermissionedComponent>
              <IconButton size="small" onClick={closeStickyVerify}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <Box>
            Verify {customerName} via SMS on {customer.phone_prefix + customer.phone}
            <br />
            🔒 SMS is the <strong>most secure</strong> way to verify, and <strong>required whenever possible</strong>.
            <br />
            After SMS, no other verification step is required.
          </Box>
        </Alert>
      </Container>

      <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        {currentStep === STEP_SEND_SMS && (
          <SendSmsModal
            onClose={handleCloseModal}
            setCurrentStep={setCurrentStep}
            phonePrefix={customer.phone_prefix}
            phone={customer.phone}
            customerId={customerId}
            customerName={customerName}
          />
        )}
        {currentStep === STEP_ENTER_CODE && (
          <EnterCodeModal
            onClose={handleCloseModal}
            setCurrentStep={setCurrentStep}
            phonePrefix={customer.phone_prefix}
            phone={customer.phone}
            customerId={customerId}
            onVerificationSuccess={handleVerificationSuccess}
          />
        )}
        {currentStep === STEP_SUCCESS && <OtpSuccessModal onClose={handleCloseModal} />}
      </Dialog>
    </Box>
  );
}

export default StickyVerifyCustomer;
